import * as Sentry from '@sentry/browser'

import buildUrl from 'modules/core/lib/buildUrl'
import { downloadBlob } from 'modules/core/lib/download'
import { BusinessError } from 'modules/core/lib/errors'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import app from 'modules/core/store/models/app'
import type { FlashType } from 'modules/core/store/models/app'

import getTitle from 'modules/documents/components/Documents/getTitle'
import folders from 'modules/documents/store/collections/folders'

const DEFAULT_FLASH_TIMEOUT_MS = 30_000

export async function downloadFile(queryParams: string, data: any) {
  const sendExportLocation = `/payroll_exporter/export/downloadable`
  const downloadErrorsPath = '/payroll_exporter/export/download_errors'
  const downloadErrorsLocation = `${downloadErrorsPath}?${queryParams}`
  const errorsLocation = `/payroll_exporter/export/errors?${queryParams}`
  const downloadPath = '/payroll_exporter/export/download'
  const downloadLocation = `${downloadPath}?${queryParams}`
  const api = ApiClient()

  const showFlash = function (
    type: FlashType,
    message: string,
    time: number = DEFAULT_FLASH_TIMEOUT_MS
  ) {
    app.setFlash({ type, message, time })
  }

  const manageErrorResult = async function (payrollFolder: string) {
    // check if there were errors during the export
    const error = await api.get(errorsLocation)

    if (error.result) {
      // Download error file
      await processDownload(downloadErrorsLocation, downloadErrorsPath)
      showFlash('accent', i18n.t('payroll_bundle.export.download_with_errors'))
    } else {
      showFlash(
        'correct',
        i18n.t('payroll_bundle.export.download_folder', {
          payrollFolder,
        })
      )
    }
  }

  /* disposition param is a string which constains the follow structure:
    'attachment; filename="example.xlsx"; filename*=UTF-8''example.xlsx'
    and we use regexp to retrieve the filename value
    */
  function getFileName(disposition: string) {
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    const matches = filenameRegex.exec(disposition)

    if (matches != null && matches[1]) {
      return matches[1].replace(/['"]/g, '')
    }
  }

  async function processDownload(location: string, path: string) {
    // TODO: cache: 'reload' can be removed after ~3 months from the merged of this.
    // It was added to fix a bug where the browser was caching the file and not downloading the new one.
    const res = await fetch(buildUrl(location), { credentials: 'include', cache: 'reload' })

    if (!res.ok) {
      throw new Error(`Error fetching ${path}. Status: ${res.status}`)
    }

    const disposition = res.headers.get('content-disposition') ?? ''

    if (!disposition || !disposition.includes('attachment')) {
      throw new Error(
        `Error processing ${path} response. Content-Disposition header is expected to contain attachment value`
      )
    }

    const filename = getFileName(disposition)

    if (!filename) {
      throw new Error(
        `Error processing ${path} response. Content-Disposition header is expected to contain filename value`
      )
    }

    const blob = await res.blob()
    downloadBlob(blob, filename)
  }

  try {
    const payload = await api.post(sendExportLocation, data)

    // Gets the export folder name as displayed to the user in Files
    // (see components/Documents/Folders/Item/index.tsx)
    await folders.fetch()

    const payrollFolder = getTitle(folders.payrollExportsFolder?.attributes.name)

    if (payload.result) {
      await processDownload(downloadLocation, downloadPath)
      manageErrorResult(payrollFolder)
    } else {
      // Export will be generated on background and sent by email
      showFlash(
        'correct',
        i18n.t('payroll_bundle.export.download_by_email_and_folder', {
          payrollFolder,
        })
      )
    }
  } catch (_error) {
    let message: string
    if (_error instanceof BusinessError) {
      message = _error.message
    } else {
      message = i18n.t('payroll_bundle.export.error')
    }
    showFlash('wrong', message)
    Sentry.captureException(_error)
  }
}
